import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Form, Button, Alert, Image, Spinner } from "react-bootstrap";
import { fetchWithAuth, extractErrorMessage  } from "../../Functions";
import "../../assets/scss/components/custom-form.scss";

function AddTeamMember () {
    const API = process.env.REACT_APP_API_BASE_URL;
    const initialFormState = {
        name: "",
        role: "",
        image: null,
        // variable names from here are same as API's (snakecase) to avoid conflicts
        linkedin_social: "",
        instagram_social: "",
        facebook_social: "",
        twitter_social: "",
    }
    const [formState, setFormState] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false); // For form submission
    const navigate = useNavigate();
    const [formFeedback, setFormFeedback] = useState({ success: null, message: "" });
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const [previewImage, setPreviewImage] = useState(null);  // For image preview on select

    // For team member update
    const location = useLocation();
    const teamMemberID = location?.state?.memberID;
    const [teamMember, setTeamMember] = useState(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setFormState((prev) => ({...prev, image: file}));

        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setPreviewImage(reader.result);
            reader.readAsDataURL(file);
        } else {
            setPreviewImage(null); // Clear preview if no file is selected
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }

    const fetchTeamMember = useCallback(async (memberID) => {
        try {
            const response = await fetch(`${API}/teams/${memberID}/`);

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // Navigate to login page
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            if (!response.ok) {
                const errorData = await response.json();
                setFetchFeedback({ success: false, message: "Unable to load team member, please refresh page."});
                console.error(errorData);
            }

            const teamMemberData = await response.json();

            setTeamMember(teamMemberData);
            setFetchFeedback({ success: true, message: ""});
            return teamMemberData;
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error occured loading team member."});
            console.error(error);
        }
    }, [API, navigate]);

    // For converting team member data into comparable state
    const getComparableState = (data) => {
        const { name, role, linkedin_social, instagram_social, twitter_social, facebook_social } = data;
        return { name, role, linkedin_social, instagram_social, twitter_social, facebook_social };
    }

    // Fetch team member data if updating
    useEffect(() => {
        if (teamMemberID) {
            fetchTeamMember(teamMemberID);
        }
    }, [teamMemberID, fetchTeamMember]);

    // Load team member data into form if updating
    useEffect(() => {
        if (teamMember) {
            setFormState({
                name: teamMember.name,
                role: teamMember.role,
                image: teamMember.image,
                linkedin_social: teamMember.linkedin_social,
                instagram_social: teamMember.instagram_social,
                facebook_social: teamMember.facebook_social,
                twitter_social: teamMember.twitter_social,
            })
            setPreviewImage(teamMember.image);
        }
    }, [teamMember, API]);

    const handleSubmit = async(e) => {
        e.preventDefault();
        setFormFeedback({ success: null, message: "" }); // Reset form feedback

        // If UPDATE: Check that changes were detected before calling API
        if (teamMember) {
            if (JSON.stringify(getComparableState(formState)) === JSON.stringify(getComparableState(teamMember))
                && !(formState.image instanceof File)) {
                setFormFeedback({ success: false, message: "No changes detected." });
                return;
            }
        }

        setIsLoading(true);

        // Build form data
        const formData = new FormData();

        // Append all form fields except "image". "image" to be handled separately
        Object.keys(formState).forEach((key) => {
            if (key !== "image"){
                formData.append(key, formState[key]);
            }
        });

        // Append image to formData only if it's a file and not a URL
        if (formState.image instanceof File) {
            // console.log("Inside image handler block")
            formData.append("image", formState.image);
        }

        // Check formData content matches formState
        // console.log(formState);
        // for (const [key, value] of formData.entries()) {
        //     console.log(`${key}: ${value}`);
        // }

        // Send form data to API
        const isEdit = Boolean(teamMember);
        const url = isEdit ? `${API}/teams/update/${teamMemberID}/` : `${API}/teams/create/`;
        const method = isEdit ? 'PUT' : 'POST';

        try {
            const response = await fetchWithAuth(url, {
                method: method,
                body: formData,
            });

            const data = await response.json();

            // Handle 401
            if (response.status === 401) {
                console.error("Session expired. Redirecting to admin login page.");

                // Set a message to inform the user before the redirect
                setFormFeedback({
                    success: false,
                    message: "Your session has expired. Please log in again."
                });

                // This is the line that performs the navigation
                navigate("/admin", { replace: true });

                // Stop further execution of the function
                return; 
            }

            // Handle response
            if (response.ok) {
                setFormFeedback({
                    success: true,
                    message: isEdit ? "Team member successfully updated!" : "Team member successfully added!"
                });

                // Clear form on successful ADDITION of team member only
                if (!isEdit){
                    setFormState(initialFormState);
                    setPreviewImage(null);
                }
            } else {
                const defaultErrorMessage = `Unable to ${ isEdit ? 'update' : 'add'} team member, please try again.`
                setFormFeedback({
                    success: false,
                    message: extractErrorMessage(data) || defaultErrorMessage,
                });

                // Log error to console
                console.error(data);
            }
        } catch (error) {
            setFormFeedback({
                success: false,
                message: extractErrorMessage(error) || "An unexpected error has occured."
            });
            console.error(error);
        } finally {
            setIsLoading(false);
        };
    };

    return (
        <>
        <h1 className="my-4 px-4 fw-semibold mb-4">{teamMember ? 'Update Team Member' : 'Add Team Member'}</h1>

        {/* Fetch Listing Alert */}
        { fetchFeedback.success === false && // Display this alert only when fetchListings fails
            <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
        }

        <p className="px-4 fs-6">Fill the form below to {teamMember ? 'update' : 'add a new'} team member.</p>
        <Form onSubmit={ handleSubmit } className="dashboard-form bg-white px-4 pt-3 pb-3">
            {/* Name */}
            <Form.Group controlId="name" className="mb-4">
                <Form.Label>Name<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label> {/* Replace * with (required) */}
                <Form.Control
                    type="text"
                    name="name"
                    value={ formState.name }
                    onChange={ handleChange }
                    required
                />
            </Form.Group>

            {/* Role */}
            <Form.Group controlId="role" className="mb-4">
                <Form.Label>Role<small className="required text-danger fst-italic ms-1">&#40;required&#41;</small></Form.Label> {/* Replace * with (required) */}
                <Form.Control
                    type="text"
                    name="role"
                    value={ formState.role }
                    onChange={ handleChange }
                    required
                />
            </Form.Group>

            {/* LinkedIn & Twitter */}
            <Row xs={1} md={2}>
                <Form.Group controlId="linkedin" className="mb-4">
                    <Form.Label>LinkedIn</Form.Label>
                    <Form.Control
                        type="text"
                        name="linkedin_social"
                        value={ formState.linkedin_social }
                        onChange={ handleChange }
                    />
                </Form.Group>

                <Form.Group controlId="twitter" className="mb-4">
                    <Form.Label>Twitter</Form.Label>
                    <Form.Control
                        type="text"
                        name="twitter_social"
                        value={ formState.twitter_social }
                        onChange={ handleChange }
                    />
                </Form.Group>
            </Row>

            {/* Instagram & Facebook */}
            <Row xs={1} md={2}>
                <Form.Group controlId="instagram" className="mb-4">
                    <Form.Label>Instagram</Form.Label>
                    <Form.Control
                        type="text"
                        name="instagram_social"
                        value={ formState.instagram_social }
                        onChange={ handleChange }
                    />
                </Form.Group>

                <Form.Group controlId="facebook" className="mb-4">
                    <Form.Label>Facebook</Form.Label>
                    <Form.Control
                        type="text"
                        name="facebook_social"
                        value={ formState.facebook_social }
                        onChange={ handleChange }
                    />
                </Form.Group>
            </Row>

            {/* File */}
            <Form.Group controlId="image" className="mb-4 preview-image-container">
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={ handleImageChange }
                    required={!teamMember} // Make field required only on team member addition
                />
                { previewImage &&
                    <Image src={previewImage} alt="Selected Image" className="preview-image mt-4" rounded />
                }
            </Form.Group>

            <Button type="submit" className="px-3 px-md-5 mb-4">{teamMember ? 'Update' : 'Add member'}</Button>
            <br></br>
            {isLoading === true &&
                <div className="d-inline-block px-3 my-3">
                    <Spinner animation="border" className="d-flex align-self-center mx-auto" variant="primary" />
                    <p className="mt-2 text-center">{ teamMember ? "Updating team member" : "Adding team member"}...</p>
                </div>
            }

            { formFeedback.success !== null &&
                <Alert variant={formFeedback.success ? "primary" : "danger"} className="text-center">{ formFeedback.message }</Alert>
            }
        </Form>
        </>
    )
}

export default AddTeamMember;
